(() => {
  if (customElements.get('kr-product-reviews')) return;
  const API = 'https://api.trustifyreview.com';
  const imageUrl = value => {
    if (typeof value !== 'string') return null;
    try {
      const url = new URL(value, location.origin);
      const host = url.hostname.toLowerCase();
      return url.protocol === 'https:' && !url.username && !url.password && (host === 'cdn.shopify.com' || host.endsWith('.shopifycdn.com') || host.endsWith('.trustifyreview.com')) ? url.href : null;
    } catch { return null; }
  };
  const imageKey = value => {
    const safe = imageUrl(value);
    if (!safe) return '';
    const url = new URL(safe);
    return url.origin + url.pathname.replace(/_\d+x\d*(?=\.[a-z]+$)/i, '');
  };
  class ProductReviews extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      if (!/^\d+$/.test(this.dataset.productId || '') || !/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i.test(this.dataset.shop || '')) return;
      this.started = true;
      this.items = [];
      this.rating = this.querySelector('[data-kr-rating]');
      this.reviewRoot = document.querySelector('.trustify-review-box');
      if (this.reviewRoot) {
        this.reviewRoot.classList.add('kr-product-review-scope');
        this.reviewRoot.id = 'kr-product-reviews';
      }
      this.rating?.addEventListener('click', event => {
        if (!this.reviewRoot) return;
        event.preventDefault();
        this.reviewRoot.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        this.reviewRoot.setAttribute('tabindex', '-1');
        this.reviewRoot.focus({ preventScroll: true });
      });
      this.handlePhotoClick = event => this.openPhoto(event);
      document.addEventListener('click', this.handlePhotoClick, true);
      this.loadRating();
      this.loadPhotos();
    }
    async request(path, parameters = {}) {
      const url = new URL(path, API);
      url.search = new URLSearchParams({ shop: this.dataset.shop, product_id: this.dataset.productId, ...parameters }).toString();
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { credentials: 'omit', mode: 'cors', signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw Error('Review request failed');
        const body = await response.json();
        if (body.error) throw Error('Review request failed');
        return body;
      } finally { clearTimeout(timer); }
    }
    async loadRating() {
      try {
        const body = await this.request('/reviews/statistic');
        const count = Number(body.data?.total_rating), average = Number(body.data?.rating_avg);
        if (!Number.isInteger(count) || count < 0 || !Number.isFinite(average) || average < 0 || average > 5 || !this.rating) return;
        this.querySelector('[data-kr-rating-fill]').style.width = (count ? average * 20 : 0) + '%';
        this.querySelector('[data-kr-rating-average]').textContent = count ? average.toFixed(1) : '';
        this.querySelector('[data-kr-rating-count]').textContent = count ? `(${count})` : this.dataset.emptyLabel;
        this.rating.setAttribute('aria-label', count ? this.dataset.ratingLabel.replace('[rating]', average.toFixed(1)).replace('[count]', String(count)) : this.dataset.emptyLabel);
        this.rating.hidden = false;
      } catch { /* Keep the native review widget available if summary loading fails. */ }
    }
    async loadPhotos() {
      if (this.loadingPhotos) return;
      this.loadingPhotos = true;
      try {
        const items = [], seen = new Set();
        let page = 1, pages = 1;
        do {
          const body = await this.request('/reviews', { sort_by: 'created_at:desc', limit: '50', page: String(page), rating: '', is_image: '', is_video: '', search: '' });
          if (!Array.isArray(body.data)) throw Error('Invalid review list');
          for (const review of body.data) {
            if (String(review.product_id) !== this.dataset.productId) continue;
            for (const media of Array.isArray(review.images) ? review.images : []) {
              const src = imageUrl(media.image_url) || imageUrl(media.image);
              const thumbnail = imageUrl(media.thumbnail_url) || src;
              const key = `${review.id}:${imageKey(src)}`;
              if (!src || seen.has(key)) continue;
              seen.add(key);
              items.push({ src, thumbnail, alt: 'Customer review photo', review, matchKeys: [src, thumbnail, media.image, media.fallback_url].map(imageKey).filter(Boolean) });
            }
          }
          pages = Number(body.pagination?.total_pages);
          if (!Number.isInteger(pages) || pages < 0 || Number(body.pagination?.page) !== page) throw Error('Invalid pagination');
          this.items = items.slice();
          page += 1;
        } while (page <= pages && this.isConnected);
      } catch { /* Native photo viewing remains the fallback while the service is unavailable. */ }
      finally { this.loadingPhotos = false; }
    }
    openPhoto(event) {
      if (!this.isConnected || !this.reviewRoot || !this.reviewRoot.contains(event.target) || typeof window.KinriotReviewLightbox?.open !== 'function' || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const opener = target.closest('.js-tr-open-media, .tr-review-pin-photo__item-opener, .tr-review-pin-photo__item--more');
      if (!opener || /video|youtube|tiktok/i.test(opener.getAttribute('data-media-type') || '')) return;
      if (!this.items.length) { this.loadPhotos(); return; }
      const image = target.closest('img') || opener.querySelector('img');
      const id = target.closest('[data-review-id]')?.getAttribute('data-review-id') || image?.getAttribute('data-review-id');
      const keys = [image?.currentSrc, image?.getAttribute('src'), image?.getAttribute('data-src')].map(imageKey).filter(Boolean);
      let candidates = this.items.map((item, index) => ({ item, index })).filter(({ item }) => (!id || String(item.review.id) === id) && item.matchKeys.some(key => keys.includes(key)));
      if (candidates.length !== 1 && id) {
        const mediaIndex = Number(opener.getAttribute('data-media-index'));
        const sameReview = this.items.map((item, index) => ({ item, index })).filter(({ item }) => String(item.review.id) === id);
        candidates = opener.hasAttribute('data-media-index') && Number.isInteger(mediaIndex) && sameReview[mediaIndex] ? [sameReview[mediaIndex]] : [];
      }
      const allPhotos = Boolean(target.closest('.tr-review-pin-photo__item--more'));
      if (candidates.length !== 1 && !allPhotos) return;
      try {
        if (window.KinriotReviewLightbox.open({ items: this.items, index: allPhotos ? 0 : candidates[0].index, details: !allPhotos, trigger: opener }) === true) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      } catch { /* Leave the native viewer available if the enhanced dialog cannot open. */ }
    }
  }
  customElements.define('kr-product-reviews', ProductReviews);
})();
